import Check from '@mui/icons-material/Check';
import Edit from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Typography from '@mui/material/Typography';
import { AddOnType, RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { AddOnCharge } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import useInvoiceLineItems from '~hooks/useInvoiceLineItems/useInvoiceLineItems';
import { Invoice } from '~hooks/useInvoices/models';
import { Job } from '~hooks/useJob/models';
import { DriverPayRowDef } from '~pages/Approvals/DriverPay/driversPayDataGridColumnsDefinition';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { toDecimal } from '~utils/decimal';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  LineItemsTableFooterItem,
  LineItemsTableSkeleton,
} from './components/lineItemsTableComponents';
import FlatRateLineItemsTable from './FlatRateLineItemsTable';
import { invoiceHelpers, isLineEditable, roundToTwoDecimals } from './helpers';
import HourlyRateLineItemsTable from './HourlyRateLineItemsTable';
import { LineItemsFormHandler } from './LineItemsFormHandler';
import LoadRateLineItemsTable from './LoadRateLineItemsTable';
import TonRateLineItemsTable from './TonRateLineItemsTable';

const WAIT_BEFORE_SKELETONS_IN_MS = 1000;

interface InvoiceLineItemsProps {
  invoice: Invoice;
  job: Job;
  utils: DriverPayRowDef['row']['utils'];
  canEdit: boolean;
  onEditChange: (isEditing: boolean) => void;
}

const InvoiceLineItems = observer(function InvoiceLineItems({
  invoice,
  job,
  utils,
  canEdit,
  onEditChange,
}: InvoiceLineItemsProps) {
  const theme = useTheme();
  const invoiceId = String(invoice.id);
  const { invoiceLineItemsStore, addOnsStore, userStore } = useStores();
  const {
    isLoading: isLoadingLineItems,
    getAllInvoiceLineItems,
    batchUpdateInvoiceLineItems,
  } = useInvoiceLineItems();
  const {
    getAllAddOns,
    getAllInvoiceAddOnCharges,
    isLoading: isLoadingAddOns,
    batchUpdateAddOnCharges,
  } = useAddOns();
  const [showSkeletons, setShowSkeletons] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const lineItemsFormRef = useRef<LineItemsFormHandler>({});

  const companyId = getEffectiveUserCompanyId(userStore);
  const [isUpdating, setIsUpdating] = useState(false);
  const isLoading = isLoadingLineItems || isLoadingAddOns;

  const lineItems = useMemo(() => {
    return invoiceLineItemsStore.invoiceLineItemsByInvoiceId[invoiceId] ?? [];
  }, [invoiceId, invoiceLineItemsStore.invoiceLineItemsByInvoiceId[invoiceId]]);

  const addOnCharges = useMemo(() => {
    return addOnsStore.addOnChargesByInvoiceId[invoiceId] ?? [];
  }, [invoiceId, addOnsStore.addOnChargesByInvoiceId[invoiceId]]);

  // Each line says whether it can be edited, so an invoice with an approved line and a
  // draft one is still editable. An invoice with no lines yet follows its own flag.
  const hasNoLines = lineItems.length + addOnCharges.length === 0;
  const canEditLineItems = hasNoLines
    ? invoice.editable
    : [...lineItems, ...addOnCharges].some((line) => isLineEditable(line, invoice));

  useEffect(() => {
    // Skip while editing so an RTU flag flip doesn't clobber the edit-mode
    // split rows. When isEditing flips back to false, this effect re-runs and
    // restores the read-mode view.
    if (isEditing) return;
    getAllInvoiceLineItems(companyId, invoiceId, invoice.materialRateItemized);
    getAllInvoiceAddOnCharges(companyId, invoiceId);
  }, [invoiceId, companyId, invoice.materialRateItemized, isEditing]);

  useEffect(() => {
    if (isEditing) {
      getAllAddOns(companyId).then((response) => {
        const selectableAddOns = response.filter((addOn) => {
          return addOn.addOnTypes.includes(AddOnType.INVOICING);
        });

        addOnsStore.setSelectableAddOnsByInvoiceId(invoiceId, selectableAddOns);
      });
    }
  }, [isEditing, companyId, invoiceId]);

  useEffect(() => {
    // Wait some time before showing the loading skeletons, or the UX when loading
    // The page may feel a little funky
    const timeout = setTimeout(() => {
      setShowSkeletons(isLoading);
    }, WAIT_BEFORE_SKELETONS_IN_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [isLoading]);

  useEffect(() => {
    // Lock the store while editing so RTU-triggered refetches can't overwrite
    // the in-edit split rows with read-mode (combined) data.
    if (!isEditing) return;
    invoiceLineItemsStore.setEditLockForInvoice(invoiceId, true);
    return () => {
      invoiceLineItemsStore.setEditLockForInvoice(invoiceId, false);
    };
  }, [isEditing, invoiceId, invoiceLineItemsStore]);

  const handleEditChange = async (isEditing: boolean) => {
    if (isEditing) {
      // Fetch add-on charges first — its store is view-agnostic. Line items
      // must be last so a mid-sequence failure never commits split rows to
      // the store while the user stays in read mode. Separate try/catch so
      // each failure surfaces the right message.
      try {
        await getAllInvoiceAddOnCharges(companyId, invoiceId);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.add_ons.failed_to_fetch_charges'),
          );
        } catch {
          // Error already reported via toast + Sentry.
        }
        return;
      }
      try {
        await getAllInvoiceLineItems(companyId, invoiceId, true);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.invoices.failed_to_fetch_line_items'),
          );
        } catch {
          // Error already reported via toast + Sentry.
        }
        return;
      }
      setIsEditing(true);
      onEditChange(true);
      return;
    }

    // Exit edit mode; the mount useEffect re-fetches with the invoice's flag
    // once isEditing flips back to false.
    setIsEditing(false);
    onEditChange(false);
  };

  const onSubmit = async () => {
    setIsUpdating(true);
    try {
      const formData = await lineItemsFormRef.current?.onSubmit?.();

      const lockedLineItemIds = new Set(
        lineItems
          .filter((lineItem) => !isLineEditable(lineItem, invoice))
          .map((lineItem) => lineItem.id),
      );
      const editableLineItems = (formData?.lineItems ?? []).filter(
        (lineItem) => !lockedLineItemIds.has(String(lineItem.id)),
      );

      if (editableLineItems.length) {
        await batchUpdateInvoiceLineItems(
          companyId,
          String(invoice.id),
          editableLineItems as Array<Partial<InvoiceLineItem>>,
        );
      }

      if (formData?.addOnCharges.length) {
        await batchUpdateAddOnCharges(
          companyId,
          String(invoice.id),
          formData.addOnCharges,
        );
      }

      await handleEditChange(false);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography>{t('approvals.driver_pay.tabs.driver_pay.line_items')}</Typography>
          {canEditLineItems && !isEditing ? (
            <IconButton
              color="secondary"
              onClick={() => handleEditChange(true)}
              disabled={!canEdit}
              sx={{
                border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                borderRadius: theme.spacing(0.5),
                ml: 1,
              }}
            >
              <Edit sx={{ height: 20, width: 20 }} />
            </IconButton>
          ) : null}
        </Box>

        {isEditing ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button
              color="secondary"
              variant="outlined"
              onClick={() => handleEditChange(false)}
              disabled={isUpdating}
            >
              {t('actions.cancel')}
            </Button>

            <Button
              color="success"
              loading={isUpdating}
              startIcon={<Check />}
              onClick={onSubmit}
              disabled={isUpdating}
            >
              {t('actions.save')}
            </Button>
          </Box>
        ) : null}
      </Box>
      {isLoading ? (
        <>{showSkeletons && <LineItemsTableSkeleton />}</>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <LineItemsTable
            ref={lineItemsFormRef}
            addOnCharges={addOnCharges}
            invoice={invoice}
            isEditing={isEditing}
            isUpdating={isUpdating}
            job={job}
            lineItems={lineItems}
            utils={utils}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row-reverse',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <LineItemsTableFooter invoice={invoice} lineItems={lineItems} utils={utils} />
          </Box>
        </Box>
      )}
    </Box>
  );
});

interface LineItemsTableProps {
  addOnCharges: AddOnCharge[];
  invoice: Invoice;
  isEditing?: boolean;
  isUpdating?: boolean;
  job: Job;
  lineItems: InvoiceLineItem[];
  utils: DriverPayRowDef['row']['utils'];
}

const LineItemsTable = forwardRef<LineItemsFormHandler, LineItemsTableProps>(
  function LineItemsTable(
    { addOnCharges, invoice, isEditing, isUpdating, job, lineItems, utils },
    ref,
  ) {
    const theme = useTheme();

    return (
      <TableContainer>
        <Table
          sx={{
            tableLayout: 'fixed',
            border: `solid 1px ${theme.palette.divider}`,
            borderCollapse: 'separate',
            borderRadius: theme.spacing(0.5, 0.5, 0, 0.5),
            overflow: 'hidden',
          }}
        >
          {invoice.rateType === RateType.RATE_PER_HOUR ? (
            <HourlyRateLineItemsTable
              ref={ref}
              addOnCharges={addOnCharges}
              invoice={invoice}
              isEditing={isEditing}
              isUpdating={isUpdating}
              job={job}
              lineItems={lineItems}
              utils={utils}
            />
          ) : invoice.rateType === RateType.RATE_PER_LOAD ? (
            <LoadRateLineItemsTable
              ref={ref}
              addOnCharges={addOnCharges}
              invoice={invoice}
              isEditing={isEditing}
              isUpdating={isUpdating}
              job={job}
              lineItems={lineItems}
              utils={utils}
            />
          ) : invoice.rateType === RateType.RATE_FLAT_RATE ? (
            <FlatRateLineItemsTable
              ref={ref}
              addOnCharges={addOnCharges}
              invoice={invoice}
              isEditing={isEditing}
              isUpdating={isUpdating}
              job={job}
              lineItems={lineItems}
              utils={utils}
            />
          ) : (
            <TonRateLineItemsTable
              ref={ref}
              addOnCharges={addOnCharges}
              invoice={invoice}
              isEditing={isEditing}
              isUpdating={isUpdating}
              job={job}
              lineItems={lineItems}
              utils={utils}
              invoiceRateType={invoice.rateType}
            />
          )}
        </Table>
      </TableContainer>
    );
  },
);

interface LineItemsTableFooterProps {
  invoice: Invoice;
  lineItems: InvoiceLineItem[];
  utils: DriverPayRowDef['row']['utils'];
}

export const LineItemsTableFooter = observer(
  ({ invoice, lineItems, utils }: LineItemsTableFooterProps) => {
    const theme = useTheme();
    const invoiceId = String(invoice.id);
    const { invoiceLineItemsStore, addOnsStore } = useStores();
    const taxTotal = invoice.taxTotalAmount;

    const [subtotal, commission, addOns, total] = useMemo(() => {
      const subtotal = invoiceLineItemsStore.invoiceSubtotalByInvoiceId[invoiceId];
      const commission = lineItems
        .reduce(
          (sum, lineItem) => sum.plus(toDecimal(lineItem.payCommissionAmount)),
          toDecimal(0),
        )
        .toNumber();
      const addOns = addOnsStore.addOnChargesTotalByInvoiceId[invoiceId];
      const pretaxTotal = invoiceHelpers.calculateInvoiceTotal(
        subtotal + commission,
        addOns,
      );
      const total = roundToTwoDecimals(pretaxTotal + (taxTotal ?? 0));

      return [subtotal, commission, addOns, total];
    }, [
      lineItems,
      invoiceLineItemsStore.invoiceSubtotalByInvoiceId[invoiceId],
      addOnsStore.addOnChargesTotalByInvoiceId[invoiceId],
      taxTotal,
    ]);

    return (
      <Box
        sx={{
          width: '350px',
          bgcolor: theme.semantic.surfaceSunken,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `solid 1px ${theme.palette.divider}`,
          borderRight: `solid 1px ${theme.palette.divider}`,
          borderBottom: `solid 1px ${theme.palette.divider}`,
          borderRadius: theme.spacing(0, 0, 0.5, 0.5),
          py: 1.5,
          gap: 1.5,
        }}
      >
        <LineItemsTableFooterItem
          label={t('common.subtotal')}
          value={utils.currencyFormatter(subtotal)}
        />
        {commission !== 0 && (
          <LineItemsTableFooterItem
            label={t('pay_commission_statement.deductions')}
            value={utils.currencyFormatter(commission)}
          />
        )}
        <LineItemsTableFooterItem
          label={t('common.add_ons')}
          value={utils.currencyFormatter(addOns)}
        />
        {taxTotal ? (
          <LineItemsTableFooterItem
            label={t('approvals.driver_pay.settlements.tax')}
            value={utils.currencyFormatter(taxTotal)}
          />
        ) : null}
        <Divider />
        <LineItemsTableFooterItem
          label={t('approvals.driver_pay.tabs.driver_pay.total')}
          value={utils.currencyFormatter(total)}
          size="large"
        />
      </Box>
    );
  },
);

export default InvoiceLineItems;
